// src/admin/AdminPros.jsx - TRIMMED VERSION (ends at line with closing })
import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { supabase } from "src/lib/supabase";
import ProKpiStrip from "./pros/ProKpiStrip";
import ProsTable from "./pros/ProsTable";
import ProDrawer from "./pros/ProDrawer";
import ProFilterDrawer from "./pros/ProFilterDrawer";
import AddProModal from "./pros/AddProModal";
import BulkActionBar from "./pros/BulkActionBar";

// Pure filter function
function applyFilters(data, query, kpi, filters, sortField, sortDir) {
  let result = data.filter(p => {
    const q = query.toLowerCase();
    if (q && !`${p.name} ${p.phone} ${p.email} ${p.id}`.toLowerCase().includes(q)) return false;
    if (!filters.status[p.status]) return false;
    if (!filters.categories.has(p.category)) return false;
    if (p.rating < filters.ratingMin) return false;
    if (filters.earningsMin !== "" && p.earnings < Number(filters.earningsMin)) return false;
    if (filters.earningsMax !== "" && p.earnings > Number(filters.earningsMax)) return false;
    if (filters.dateFrom && p.joined < filters.dateFrom) return false;
    if (filters.dateTo && p.joined > filters.dateTo) return false;
    return true;
  });

  if (kpi === "active") result = result.filter(p => p.status === "active");
  if (kpi === "pending") result = result.filter(p => p.status === "pending");
  if (kpi === "suspended") result = result.filter(p => p.status === "suspended");
  if (kpi === "top") result = result.filter(p => p.earnings >= 20000);

  if (sortField) {
    result = [...result].sort((a, b) => {
      const av = a[sortField], bv = b[sortField];
      if (typeof av === "string") return sortDir * av.localeCompare(bv);
      return sortDir * (av - bv);
    });
  }
  return result;
}

export default function AdminPros() {
  const [pros, setPros] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectedPro, setSelectedPro] = useState(null);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeKpi, setActiveKpi] = useState("all");
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  
  const [filters, setFilters] = useState({
    status: { active: true, pending: true, suspended: true },
    categories: new Set(),
    ratingMin: 0,
    earningsMin: "",
    earningsMax: "",
    dateFrom: "",
    dateTo: "",
  });

  const PAGE_SIZE = 10;
  const searchTimer = useRef(null);

  const loadPros = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchErr } = await supabase
        .from("pro_profiles")
        .select(`
          id, account_status, verified, base_price, avg_rating, review_count,
          completed_jobs, availability, category, created_at,
          profiles!user_id ( full_name, phone, email )
        `)
        .order("created_at", { ascending: false });

      if (fetchErr) throw fetchErr;

      // Real earnings: sum of NET payout (after 25% platform fee) for paid,
      // completed job_requests per pro. This is what the pro actually
      // receives — customers never see this fee, only pros/admins do.
      const { data: earningsRows, error: earnErr } = await supabase
        .from("job_requests")
        .select("pro_id, estimated_price, platform_fee")
        .eq("payment_status", "paid")
        .eq("status", "completed");
      if (earnErr) console.error("Failed to load earnings:", earnErr); // non-fatal — show 0 rather than block the page

      const earningsByPro = {};
      (earningsRows || []).forEach(r => {
        const net = (r.estimated_price || 0) - (r.platform_fee || 0);
        earningsByPro[r.pro_id] = (earningsByPro[r.pro_id] || 0) + net;
      });

      const shaped = (data || []).map(p => ({
        id: p.id,
        name: p.profiles?.full_name || "Unnamed Pro",
        phone: p.profiles?.phone || "",
        email: p.profiles?.email || "",
        category: p.category || "Uncategorised",
        earnings: earningsByPro[p.id] || 0,
        rating: p.avg_rating || 0,
        status: p.account_status,
        jobsCompleted: p.completed_jobs || 0,
        lastActive: p.availability === "available" ? "Active now" : "—",
        location: "",
        joined: p.created_at,
        verification: p.verified ? "verified" : "unverified",
      }));

      setPros(shaped);
      setFilters(prev => ({
        ...prev,
        categories: new Set(shaped.map(p => p.category)),
      }));
    } catch (err) {
      console.error("Failed to load pros:", err);
      setError(err.message || "Failed to load pros");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPros(); }, [loadPros]);

  const showToast = useCallback((message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const handleSearchInput = useCallback((val) => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setSearchQuery(val), 300);
  }, []);

  const filteredPros = useMemo(() => {
    return applyFilters(pros, searchQuery, activeKpi, filters, sortField, sortDir);
  }, [pros, searchQuery, activeKpi, filters, sortField, sortDir]);

  const paginatedPros = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredPros.slice(start, start + PAGE_SIZE);
  }, [filteredPros, currentPage]);

  const totalPages = Math.ceil(filteredPros.length / PAGE_SIZE);

  const handleSort = useCallback((field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 1 ? -1 : 1);
    } else {
      setSortField(field);
      setSortDir(1);
    }
    setCurrentPage(1);
  }, [sortField]);

  const handleSelectAll = useCallback((checked) => {
    if (checked) {
      const currentPageIds = new Set(paginatedPros.map(p => p.id));
      setSelectedIds(prev => new Set([...prev, ...currentPageIds]));
    } else {
      const currentPageIds = new Set(paginatedPros.map(p => p.id));
      const newSelected = new Set(selectedIds);
      currentPageIds.forEach(id => newSelected.delete(id));
      setSelectedIds(newSelected);
    }
  }, [paginatedPros, selectedIds]);

  const handleSelect = useCallback((id, checked) => {
    if (checked) {
      setSelectedIds(prev => new Set([...prev, id]));
    } else {
      setSelectedIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  }, []);

  const handleRowClick = useCallback((pro) => {
    setSelectedPro(pro);
  }, []);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({
      status: { active: true, pending: true, suspended: true },
      categories: new Set(pros.map(p => p.category)),
      ratingMin: 0,
      earningsMin: "",
      earningsMax: "",
      dateFrom: "",
      dateTo: "",
    });
    setActiveKpi("all");
    setSearchQuery("");
    setCurrentPage(1);
    showToast("Filters cleared", "success");
  }, [pros, showToast]);

  const handleRetry = useCallback(() => {
    loadPros();
    showToast("Data reloaded", "success");
  }, [loadPros, showToast]);

  const handleBulkApprove = useCallback(async () => {
    const ids = Array.from(selectedIds);
    const prevPros = pros;
    // Optimistic update
    setPros(prev => prev.map(pro =>
      ids.includes(pro.id) ? { ...pro, verification: "verified", status: "active" } : pro
    ));
    try {
      const { error } = await supabase
        .from("pro_profiles")
        .update({ verified: true, account_status: "active" })
        .in("id", ids);
      if (error) throw error;
      showToast(`${ids.length} pro(s) approved`, "success");
    } catch (err) {
      console.error("Bulk approve failed:", err);
      setPros(prevPros); // revert on failure
      showToast(`Failed to approve: ${err.message}`, "error");
    }
    setSelectedIds(new Set());
  }, [selectedIds, pros, showToast]);

  const handleBulkSuspend = useCallback(async () => {
    const ids = Array.from(selectedIds);
    const prevPros = pros;
    setPros(prev => prev.map(pro =>
      ids.includes(pro.id) ? { ...pro, status: "suspended" } : pro
    ));
    try {
      const { error } = await supabase
        .from("pro_profiles")
        .update({ account_status: "suspended" })
        .in("id", ids);
      if (error) throw error;
      showToast(`${ids.length} pro(s) suspended`, "success");
    } catch (err) {
      console.error("Bulk suspend failed:", err);
      setPros(prevPros);
      showToast(`Failed to suspend: ${err.message}`, "error");
    }
    setSelectedIds(new Set());
  }, [selectedIds, pros, showToast]);

  const handleBulkExport = useCallback(() => {
    const selectedPros = pros.filter(p => selectedIds.has(p.id));
    const csv = [
      ["ID", "Name", "Phone", "Email", "Category", "Earnings", "Rating", "Status", "Jobs Completed", "Last Active", "Location", "Joined", "Verification"],
      ...selectedPros.map(p => [p.id, p.name, p.phone, p.email, p.category, p.earnings, p.rating, p.status, p.jobsCompleted, p.lastActive, p.location, p.joined, p.verification])
    ].map(row => row.map(cell => `"${cell}"`).join(",")).join("\n");
    
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pros_export_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showToast(`Exported ${selectedIds.size} pro(s)`, "success");
  }, [pros, selectedIds, showToast]);

  const handleAddPro = useCallback((newPro) => {
    setPros(prev => [newPro, ...prev]);
    showToast(`${newPro.name} added successfully`, "success");
    setAddModalOpen(false);
  }, [showToast]);

  const handleApprovePro = useCallback(async (proId) => {
    const prevPros = pros;
    setPros(prev => prev.map(pro =>
      pro.id === proId ? { ...pro, verification: "verified", status: "active" } : pro
    ));
    try {
      const { error } = await supabase
        .from("pro_profiles")
        .update({ verified: true, account_status: "active" })
        .eq("id", proId);
      if (error) throw error;
      showToast("Pro approved", "success");
    } catch (err) {
      console.error("Approve failed:", err);
      setPros(prevPros);
      showToast(`Failed to approve: ${err.message}`, "error");
    }
  }, [pros, showToast]);

  const handleSuspendPro = useCallback(async (proId) => {
    const prevPros = pros;
    const current = pros.find(p => p.id === proId);
    const newStatus = current?.status === "suspended" ? "active" : "suspended";
    setPros(prev => prev.map(pro =>
      pro.id === proId ? { ...pro, status: newStatus } : pro
    ));
    try {
      const { error } = await supabase
        .from("pro_profiles")
        .update({ account_status: newStatus })
        .eq("id", proId);
      if (error) throw error;
      showToast("Pro status updated", "success");
    } catch (err) {
      console.error("Suspend toggle failed:", err);
      setPros(prevPros);
      showToast(`Failed to update status: ${err.message}`, "error");
    }
  }, [pros, showToast]);

  const handleMessagePro = useCallback((proId) => {
    const pro = pros.find(p => p.id === proId);
    showToast(`Messaging ${pro?.name} - Coming soon`, "info");
  }, [pros, showToast]);

  const handlePayoutReview = useCallback((proId) => {
    const pro = pros.find(p => p.id === proId);
    showToast(`Payout review triggered for ${pro?.name}`, "info");
  }, [pros, showToast]);

  const allCategories = useMemo(() => [...new Set(pros.map(p => p.category))], [pros]);

  // Deep-link drawer support (optional)
  // Uncomment if you add the route in App.js
  // const { proId } = useParams();
  // useEffect(() => {
  //   if (proId) {
  //     const pro = pros.find(p => p.id === proId);
  //     if (pro) setSelectedPro(pro);
  //   }
  // }, [proId, pros]);

  return (
    <div style={{ maxWidth: 1280, margin: "0 auto" }}>
      {/* Toolbar */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 24,
        gap: 12,
        flexWrap: "wrap"
      }}>
        <div />
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {selectedIds.size > 0 ? (
            <BulkActionBar
              selectedCount={selectedIds.size}
              onApprove={handleBulkApprove}
              onSuspend={handleBulkSuspend}
              onExport={handleBulkExport}
              onClear={() => setSelectedIds(new Set())}
            />
          ) : (
            <>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                background: "#fff",
                borderRadius: 10,
                padding: "8px 14px",
                border: "1px solid #E2E8F0",
                width: 320
              }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2">
                  <circle cx="10" cy="10" r="7" />
                  <line x1="21" y1="21" x2="15" y2="15" />
                </svg>
                <input
                  type="text"
                  placeholder="Search by name, phone, email, or ID..."
                  onChange={(e) => handleSearchInput(e.target.value)}
                  style={{
                    border: "none",
                    outline: "none",
                    fontSize: 13.5,
                    color: "#334155",
                    width: "100%",
                    background: "transparent",
                    fontFamily: "'Plus Jakarta Sans', sans-serif"
                  }}
                />
              </div>
              <button
                onClick={() => setFilterDrawerOpen(true)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 16px",
                  background: "#fff",
                  border: "1px solid #E2E8F0",
                  borderRadius: 10,
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#64748B",
                  cursor: "pointer",
                  fontFamily: "'Plus Jakarta Sans', sans-serif"
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polygon points="22 3 2 3 10 13 10 21 14 18 14 13 22 3" />
                </svg>
                Filters
              </button>
              <button
                onClick={() => setAddModalOpen(true)}
                style={{
                  padding: "8px 20px",
                  background: "#0F172A",
                  border: "none",
                  borderRadius: 10,
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#fff",
                  cursor: "pointer",
                  fontFamily: "'Plus Jakarta Sans', sans-serif"
                }}
              >
                + Add Pro
              </button>
            </>
          )}
        </div>
      </div>

      {/* KPI Strip */}
      <ProKpiStrip
        pros={pros}
        activeKpi={activeKpi}
        onKpiClick={(kpi) => {
          setActiveKpi(activeKpi === kpi ? "all" : kpi);
          setCurrentPage(1);
        }}
      />

      {/* Table */}
      <ProsTable
        pros={paginatedPros}
        allPros={filteredPros}
        selectedIds={selectedIds}
        onRowClick={handleRowClick}
        onSelect={handleSelect}
        onSelectAll={handleSelectAll}
        onSort={handleSort}
        sortField={sortField}
        sortDir={sortDir}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        pageSize={PAGE_SIZE}
        totalPages={totalPages}
        loading={loading}
        error={error}
        onRetry={handleRetry}
        onClearFilters={handleClearFilters}
        onApprovePro={handleApprovePro}
        onSuspendPro={handleSuspendPro}
        onMessagePro={handleMessagePro}
        onPayoutReview={handlePayoutReview}
      />

      {/* Drawers & Modals */}
      <ProDrawer
        pro={selectedPro}
        onClose={() => setSelectedPro(null)}
        onUpdate={(updatedPro) => {
          setPros(prev => prev.map(p => p.id === updatedPro.id ? updatedPro : p));
          setSelectedPro(updatedPro);
          showToast(`${updatedPro.name} updated`, "success");
        }}
        showToast={showToast}
      />

      <ProFilterDrawer
        open={filterDrawerOpen}
        filters={filters}
        onApply={(newFilters) => {
          setFilters(newFilters);
          setCurrentPage(1);
          setFilterDrawerOpen(false);
          showToast("Filters applied", "success");
        }}
        onClear={() => {
          handleClearFilters();
          setFilterDrawerOpen(false);
        }}
        onClose={() => setFilterDrawerOpen(false)}
        allCategories={allCategories}
      />

      <AddProModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSave={handleAddPro}
        categories={allCategories}
      />

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed",
          bottom: 24,
          right: 24,
          background: toast.type === "success" ? "#0F172A" : toast.type === "info" ? "#3B82F6" : "#DC2626",
          color: "#fff",
          padding: "12px 20px",
          borderRadius: 10,
          fontSize: 13,
          fontWeight: 600,
          zIndex: 999,
          boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
          fontFamily: "'Plus Jakarta Sans', sans-serif"
        }}>
          {toast.message}
        </div>
      )}
    </div>
  );
}
